import { ref } from 'vue'
import { useRoute, useRouter, type LocationQuery } from 'vue-router'
import { useDebounceFn } from '@vueuse/core'
import { parseMongoFilterValueType, type MongoFilterValueType } from '@/lib/mongo-filter-value'
import { normalizeExplorerLimit } from '@/lib/explorer-limit'
import { parseExplorerSort, serializeExplorerSort } from '@/lib/explorer-sort'
import { parseExplorerAction, type ExplorerAction } from '@/lib/explorer-visual-to-raw'

export interface ExplorerFilter {
  field: string
  operator: string
  value: string
  valueType?: MongoFilterValueType
}

export interface DataExplorerUrlState {
  connectorId: string
  resource: string | null
  ia: string | null
  mode: 'visual' | 'raw'
  /** Visual action: find | count | insert | update | delete (Mongo writes gated by canMutate). */
  action: ExplorerAction
  limit: string
  filters: ExplorerFilter[]
  columns: string[]
  /**
   * Visual-mode multi-sort (SQL order_by / Mongo sort), encoded as
   * comma-separated `column:desc` entries (`:asc` omitted). Empty = unsorted.
   * See `explorer-sort.ts`.
   */
  sort: string
  rawQuery: string
  resultView: 'table' | 'json'
  jsonata: string
  csvDelimiter: string
  // Truto request-builder state (compact keys so reloads restore the builder).
  // Query-form fields keep reusing `filters`; these cover the rest.
  tmethod: string // list | get | create | update | delete | custom
  tid: string // resource id for get/update/delete
  tformat: string // truto_response_format: '' (default) | raw | debug | stream
  tqmode: 'form' | 'json' // query-param editor mode
  tbody: string // request body JSON (POST/PATCH/PUT/DELETE)
  tpath: string // Custom API path
  tverb: string // Custom API HTTP verb
}

const VALID_MODES = new Set(['visual', 'raw'])
const VALID_RESULT_VIEWS = new Set(['table', 'json'])

export function parseFilters(raw: unknown): ExplorerFilter[] {
  if (typeof raw !== 'string' || !raw) return []
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((f): f is Record<string, unknown> => f && typeof f === 'object')
      .map(f => ({
        field: String(f.field ?? ''),
        operator: String(f.operator ?? 'eq'),
        value: String(f.value ?? ''),
        valueType: parseMongoFilterValueType(f.valueType),
      }))
  } catch {
    return []
  }
}

export function parseColumns(raw: unknown): string[] {
  if (typeof raw !== 'string' || !raw) return []
  return raw.split(',').map(c => c.trim()).filter(Boolean)
}

function parseCsvDelimiter(raw: unknown): string {
  if (typeof raw !== 'string') return ''
  const alias = raw.trim().toLowerCase()
  if (alias === 'tab') return '\t'
  if (alias === '\\t') return '\t'
  if (alias === 'comma') return ','
  if (alias === 'semicolon') return ';'
  if (alias === 'pipe') return '|'
  return raw
}

function serializeCsvDelimiter(value: string): string {
  if (value === '\t') return 'tab'
  if (value === ',') return 'comma'
  if (value === ';') return 'semicolon'
  if (value === '|') return 'pipe'
  return value
}

export function parseDataExplorerUrl(route: { params: Record<string, unknown>; query: LocationQuery }): DataExplorerUrlState {
  const connectorId = route.params.connectorId ? String(route.params.connectorId) : ''
  const resource = route.query.resource ? String(route.query.resource) : null
  const ia = route.query.ia ? String(route.query.ia) : null
  const modeRaw = route.query.mode ? String(route.query.mode) : 'visual'
  const mode = VALID_MODES.has(modeRaw) ? (modeRaw as 'visual' | 'raw') : 'visual'
  const limitRaw = route.query.limit ? String(route.query.limit) : '100'
  const limit = normalizeExplorerLimit(limitRaw) ?? '100'
  const resultViewRaw = route.query.view ? String(route.query.view) : 'table'
  const resultView = VALID_RESULT_VIEWS.has(resultViewRaw) ? (resultViewRaw as 'table' | 'json') : 'table'

  const tqmodeRaw = route.query.tqmode ? String(route.query.tqmode) : 'form'
  const tqmode = tqmodeRaw === 'json' ? 'json' : 'form'
  // Legacy single-sort URLs used `?sort=col&order=desc`; fold `order` into the
  // encoded multi-sort string so the rest of the app only deals with `sort`.
  const sort = serializeExplorerSort(parseExplorerSort(route.query.sort, route.query.order))

  return {
    connectorId,
    resource,
    ia,
    mode,
    action: parseExplorerAction(route.query.action),
    limit,
    filters: parseFilters(route.query.filters),
    columns: parseColumns(route.query.columns),
    sort,
    rawQuery: route.query.raw ? String(route.query.raw) : '',
    resultView,
    jsonata: route.query.jsonata ? String(route.query.jsonata) : '',
    csvDelimiter: parseCsvDelimiter(route.query.csvdelim),
    tmethod: route.query.tmethod ? String(route.query.tmethod) : '',
    tid: route.query.tid ? String(route.query.tid) : '',
    tformat: route.query.tformat ? String(route.query.tformat) : '',
    tqmode,
    tbody: route.query.tbody ? String(route.query.tbody) : '',
    tpath: route.query.tpath ? String(route.query.tpath) : '',
    tverb: route.query.tverb ? String(route.query.tverb) : '',
  }
}

export function buildDataExplorerQuery(state: DataExplorerUrlState): LocationQuery {
  const query: LocationQuery = {}

  if (state.resource) query.resource = state.resource
  if (state.ia) query.ia = state.ia
  if (state.mode !== 'visual') query.mode = state.mode
  if (state.action && state.action !== 'find') query.action = state.action
  if (state.limit !== '100') query.limit = state.limit
  if (state.resultView !== 'table') query.view = state.resultView

  const filters = state.filters.filter(f => f.field || f.operator !== 'eq' || f.value || (f.valueType && f.valueType !== 'auto'))
  if (filters.length > 0) {
    query.filters = JSON.stringify(filters.map(f => {
      const entry: Record<string, string> = {
        field: f.field,
        operator: f.operator,
        value: f.value,
      }
      if (f.valueType && f.valueType !== 'auto') entry.valueType = f.valueType
      return entry
    }))
  }

  if (state.columns.length > 0) query.columns = state.columns.join(',')
  if (state.sort) query.sort = state.sort
  if (state.mode === 'raw' && state.rawQuery) query.raw = state.rawQuery
  if (state.jsonata) query.jsonata = state.jsonata
  if (state.csvDelimiter) query.csvdelim = serializeCsvDelimiter(state.csvDelimiter)

  // Truto request-builder params (only persist non-defaults to keep URLs lean).
  if (state.tmethod && state.tmethod !== 'list') query.tmethod = state.tmethod
  if (state.tid) query.tid = state.tid
  if (state.tformat) query.tformat = state.tformat
  if (state.tqmode === 'json') query.tqmode = state.tqmode
  if (state.tbody) query.tbody = state.tbody
  if (state.tpath) query.tpath = state.tpath
  if (state.tverb && state.tverb !== 'GET') query.tverb = state.tverb

  return query
}

/**
 * App-level query keys that coexist with explorer state on the same route
 * (e.g. `?ai=` for the AI sidebar conversation). Explorer navigations must
 * preserve them, and the explorer route watcher must ignore them — otherwise
 * switching chats rewrites `route.fullPath` and re-runs the table query.
 */
export const EXPLORER_PASSTHROUGH_QUERY_KEYS = ['ai'] as const

export function pickExplorerPassthroughQuery(query: LocationQuery): LocationQuery {
  const out: LocationQuery = {}
  for (const key of EXPLORER_PASSTHROUGH_QUERY_KEYS) {
    const v = query[key]
    if (v != null && v !== '') out[key] = v
  }
  return out
}

/** Stable key for explorer-relevant navigation — ignores passthrough params like `ai`. */
export function explorerNavigationKey(route: {
  params: Record<string, unknown>
  query: LocationQuery
}): string {
  const state = parseDataExplorerUrl(route)
  return JSON.stringify({
    connectorId: state.connectorId,
    query: buildDataExplorerQuery(state),
  })
}

/** Raw Mongo write payloads (`method: insert|update|delete`) must never auto-run. */
function isRawWriteQuery(rawQuery: string): boolean {
  const trimmed = rawQuery.trim()
  if (!trimmed.startsWith('{')) return false
  try {
    const parsed = JSON.parse(trimmed) as { method?: unknown }
    return parsed.method === 'insert' || parsed.method === 'update' || parsed.method === 'delete'
  } catch {
    return false
  }
}

export function hasExecutableQueryState(state: DataExplorerUrlState): boolean {
  // Write queries require an explicit Run (with confirm for update/delete) —
  // a shared or reloaded URL must never fire a write on load.
  if (state.mode === 'raw') {
    return state.rawQuery.trim().length > 0 && !isRawWriteQuery(state.rawQuery)
  }
  if (state.action === 'insert' || state.action === 'update' || state.action === 'delete') return false
  // Count with no filters still means "count all".
  if (state.action === 'count') return true
  // Sort alone is enough — otherwise a shared/reloaded ?sort= URL would fall
  // through to autoPreview and drop order_by / Mongo sort.
  if (state.sort) return true
  return state.filters.some(f => f.field && (f.valueType === 'null' || f.value.trim().length > 0))
}

export function useDataExplorerRoute() {
  const route = useRoute()
  const router = useRouter()
  const syncingFromRoute = ref(false)
  let skipDataReload = false

  function currentState(): DataExplorerUrlState {
    return parseDataExplorerUrl(route)
  }

  function mergeExplorerQuery(state: DataExplorerUrlState): LocationQuery {
    return {
      ...pickExplorerPassthroughQuery(route.query),
      ...buildDataExplorerQuery(state),
    }
  }

  function navigateToConnector(connectorId: string | null) {
    const passthrough = pickExplorerPassthroughQuery(route.query)
    if (!connectorId) {
      router.push({ name: 'data-explorer', query: passthrough })
      return
    }
    router.push({
      name: 'data-explorer',
      params: { connectorId },
      query: passthrough,
    })
  }

  function navigateToResource(resource: string, opts?: { resetQuery?: boolean }) {
    const connectorId = route.params.connectorId as string | undefined
    if (!connectorId) return
    const state = currentState()
    const next: DataExplorerUrlState = { ...state, resource }
    // Truto: each resource has its own schema/methods, so carrying the previous
    // request-builder state (method/id/body/params) into a new resource is wrong.
    if (opts?.resetQuery) {
      next.filters = []
      next.columns = []
      next.action = 'find'
      next.sort = ''
      next.rawQuery = ''
      next.jsonata = ''
      next.csvDelimiter = ''
      next.tmethod = 'list'
      next.tid = ''
      next.tformat = ''
      next.tqmode = 'form'
      next.tbody = ''
      next.tpath = ''
      next.tverb = 'GET'
    }
    router.push({
      name: 'data-explorer',
      params: { connectorId },
      query: mergeExplorerQuery(next),
    })
  }

  /** Truto integrated-account changes must use push (not replaceQuery) so introspect reloads. */
  function navigateToIntegratedAccount(ia: string | null) {
    const connectorId = route.params.connectorId as string | undefined
    if (!connectorId) return
    const state = currentState()
    router.push({
      name: 'data-explorer',
      params: { connectorId },
      query: mergeExplorerQuery({
        ...state,
        ia,
        resource: null,
        filters: [],
        columns: [],
        action: 'find',
        sort: '',
        rawQuery: '',
        jsonata: '',
        csvDelimiter: '',
        tmethod: 'list',
        tid: '',
        tformat: '',
        tqmode: 'form',
        tbody: '',
        tpath: '',
        tverb: 'GET',
      }),
    })
  }

  function replaceQuery(partial: Partial<DataExplorerUrlState>) {
    const next: DataExplorerUrlState = { ...currentState(), ...partial }
    skipDataReload = true
    router.replace({
      name: 'data-explorer',
      params: next.connectorId ? { connectorId: next.connectorId } : {},
      query: mergeExplorerQuery(next),
    })
  }

  function applyPresetState(partial: Partial<DataExplorerUrlState>) {
    replaceQuery(partial)
  }

  const debouncedReplaceQuery = useDebounceFn(replaceQuery, 300)

  function shouldSkipDataReload(): boolean {
    if (skipDataReload) {
      skipDataReload = false
      return true
    }
    return false
  }

  return {
    route,
    syncingFromRoute,
    currentState,
    navigateToConnector,
    navigateToResource,
    navigateToIntegratedAccount,
    replaceQuery,
    applyPresetState,
    debouncedReplaceQuery,
    shouldSkipDataReload,
  }
}
